import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { EndpointService } from '@app/core';
import { Categoria, Proceso, TramiteDefaultCorto, TramiteEstado, TramiteBase, CategoryAction, VistaDinamica, Disparador } from '@app/shared';
import { EventAction } from '../btn-trigger';
import { result } from 'lodash';

@Injectable()
export class SelectTramiteService {
  // La key corresponde a un posible valor de un objeto de tipo CategoryAction
  // El valor es la key registrada en el archivo de endpoints (./api-endpoints.ts)
  private mappingCategoryAction = {
    category: 'category',
    process: 'process',
    tramite: 'defaultTramites',
    secondaryProcess: 'adminSecondary',
  };

  constructor(private http: HttpClient, private toastr: ToastrService, private api: EndpointService) { }

  /**
   * Consulta los posibles estados de un trámite por default.
   *
   * @returns {Promise<TramiteEstado[]>}
   * @memberof SelectTramiteService
   */
  getDefaultStatusTramite(): Promise<TramiteEstado[]> {
    return new Promise((resolve, reject) => {
      this.http.get<TramiteEstado[]>(this.api.get('defaultStatusTramite')).subscribe(
        instances => {
          resolve(instances);
        },
        err => {
          reject(err);
        }
      );
    });
  }

  /**
   * Consulta los posibles estados del expediente
   *
   * @returns {Promise<TramiteBase[]>}
   * @memberof SelectTramiteService
   */
  getDefaultStatusExpediente(): Promise<TramiteBase[]> {
    return new Promise((resolve, reject) => {
      this.http.get<TramiteBase[]>(this.api.get('statusExpediente')).subscribe(
        instances => {
          resolve(instances);
        },
        err => {
          reject(err);
        }
      );
    });
  }

  /**
   * Crea o actualiza la información de una Categoria, Proceso o Tramite Default.
   *
   * @param {EventAction} resource
   * @param {*} data
   * @returns {(Promise<Categoria | Proceso | TramiteDefaultCorto | null>)}
   * @memberof SelectTramiteService
   */
  save(resource: EventAction, data: any): Promise<Categoria | Proceso | TramiteDefaultCorto | null> {
    return new Promise((resolve, reject) => {
      const verb = resource.action === 'edit' ? 'put' : 'post';
      const action = resource.action === 'edit' ? 'modificó' : 'creó';

      this.http
        .skipErrorHandler()
      [verb]<Categoria | Proceso | TramiteDefaultCorto>(
        this.api.get(this.mappingCategoryAction[resource.categoryAction], [data.id]),
        data,
        {
          observe: 'response'
        }
      ).subscribe(
        res => {
          if (res.status === 201 || res.status === 200) {
            this.toastr.success(`El registro se ${action} correctamente.`, 'Operación exitosa');
            resolve(res.body);
          }
          resolve(null);
        },
        err => {
          if (result(err, 'error.detail')) {
            this.toastr.warning(result(err, 'error.detail'), 'Alerta importante');
          } else if (result(err, 'error.message')) {
            this.toastr.warning(result(err, 'error.message'), 'Alerta importante');
          } else if (result(err, 'error.error')) {
            this.toastr.error(result(err, 'error.error'), 'Ocurrió un error');
          } else {
            this.toastr.error('Ha ocurrido un error inesperado', 'Ocurrió un error');
          }

          reject(err);
        }
      );
    });
  }

  /**
   * Elimina una Categoria, Proceso o Tramite Default.
   *
   * @param {CategoryAction} resource
   * @param {*} id
   * @returns {Promise<boolean>}
   * @memberof SelectTramiteService
   */
  delete(resource: CategoryAction, id: any): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http
        .delete<any>(this.api.get(this.mappingCategoryAction[resource], [id]), {
          observe: 'response'
        })
        .subscribe(
          res => {
            if (res.status === 200) {
              this.toastr.success(`El registro se eliminó correctamente.`, 'Operación exitosa');
              resolve(true);
            }
            resolve(false);
          },
          err => {
            if (err.status === 404) {
              if (resource === 'tramite' && err.error.hasOwnProperty('tramite')) {
                const messages = err.error.tramite;
                let concat = '';
                messages.forEach((m: any) => {
                  concat += `\n* ${m}`;
                });
                this.toastr.error(
                  `No se pudó eliminar el trámite:
                  ${concat}
                `,
                  'Operación inválida'
                );
                resolve(false);
                return;
              }
            }
            reject(err);
          }
        );
    });
  }

  /**
   * Consulta una Categoria, Proceso o Tramite Default.
   *
   * @param {CategoryAction} resource
   * @param {*} id
   * @returns {Promise<any>}
   * @memberof SelectTramiteService
   */
  get(resource: CategoryAction, id: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get<any>(this.api.get(this.mappingCategoryAction[resource], [id])).subscribe(
        data => {
          resolve(data);
        },
        err => {
          reject(err);
        }
      );
    });
  }

  /**
   * Consulta de vistas dinamicas
   *
   * @returns {Promise<VistaDinamica[]>}
   * @memberof SelectTramiteService
   */
  getDynamicViews(): Promise<VistaDinamica[]> {
    return new Promise((resolve, reject) => {
      this.http.get<VistaDinamica[]>(this.api.get('dynamicView')).subscribe(
        views => {
          resolve(views);
        },
        err => {
          reject(err);
        }
      );
    });
  }
  
  /**
   * Consulta de disparadores
   *
   * @returns {Promise<TramiteBase[]>}
   * @memberof SelectTramiteService
   */
  getTriggers(): Promise<Disparador[]> {
    return new Promise((resolve, reject) => {
      this.http.get<Disparador[]>(this.api.get('typeService')).subscribe(
        triggers => {
          resolve(triggers);
        },
        err => {
          reject(err);
        }
      );
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/administration/process-automation/shared/select-tramite.service.ts